/**
 * detectTileType — single source of truth for FileTile type routing.
 *
 * Given a (mimeType, filename) pair, returns one of the supported tile
 * types. Used by both chat renderers and the post media collage.
 *
 * Returned keys match ILLUSTRATION keys in FileTile.js.
 */

import { isMidiFile } from '../../../MediaViewer/utils/mediaTypeDetector';

const MIME_PDF = 'application/pdf';

const MIME_PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const MIME_PPT = 'application/vnd.ms-powerpoint';

const MIME_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const MIME_XLS = 'application/vnd.ms-excel';
const MIME_CSV = 'text/csv';

const MIME_MARKDOWN = ['text/markdown', 'text/x-markdown'];

const EXT = (filename) => {
  if (!filename) return '';
  const i = filename.lastIndexOf('.');
  return i === -1 ? '' : filename.slice(i).toLowerCase();
};

export function detectTileType(mimeType, filename) {
  const ext = EXT(filename);
  const mime = (mimeType || '').toLowerCase();

  // --- PDF ---
  if (mime === MIME_PDF) return 'pdf';
  if (ext === '.pdf') return 'pdf';

  // --- MIDI --- note data, not sound (Oct 29 2026): no browser can play it, so the plain file tile, never Play
  if (isMidiFile(mime, filename)) return 'generic';

  // --- Audio ---
  // mp3, wav, m4a, aac, flac, opus, ogg, weba, wma
  if (mime.startsWith('audio/')) return 'audio';
  if (['.mp3', '.wav', '.m4a', '.aac', '.flac', '.opus', '.ogg', '.weba', '.wma'].includes(ext)) {
    return 'audio';
  }

  // --- Video ---
  if (mime.startsWith('video/')) return 'video';
  if (['.mp4', '.webm', '.mov', '.avi', '.mkv', '.wmv', '.flv', '.mpeg', '.mpg', '.3gp', '.3gpp'].includes(ext)) {
    return 'video';
  }

  // --- PowerPoint ---
  if (mime === MIME_PPTX || mime === MIME_PPT) return 'pptx';
  if (['.pptx', '.ppt'].includes(ext)) return 'pptx';

  // --- Spreadsheet (Excel + CSV) ---
  if (mime === MIME_XLSX || mime === MIME_XLS || mime === MIME_CSV) return 'xlsx';
  if (['.xlsx', '.xls', '.csv'].includes(ext)) return 'xlsx';

  // --- Markdown ---
  if (MIME_MARKDOWN.includes(mime)) return 'markdown';
  if (['.md', '.markdown'].includes(ext)) return 'markdown';

  // --- Fallback ---
  return 'generic';
}

/**
 * Extract a short display label for the generic tile's extension pill.
 * Examples: "report.epub" → ".EPUB",  "archive.tar.gz" → ".GZ",  "nofile" → "FILE"
 */
export function getGenericLabel(filename) {
  const ext = EXT(filename);
  if (!ext) return 'FILE';
  // Uppercase the extension text, keep leading dot
  return ext.toUpperCase();
}
